import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SellIcon from '@mui/icons-material/Sell';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';

import { setCurrentProvision } from '../../../store/courseService';
import { loadProvisionStatusDetails } from '../../../store/directusService';
import { POLARIS_BLUE, POLARIS_VICTORIA_BLUE } from '../../../themes/theme';
import TextAreaField from '../../formElements/TextAreaField';
import LabelledTextField from '../../ui/editors/LabelledTextField';
import LoadingSpinner from '../../ui/LoadingSpinner';

import dropdownClasses from '../courseStyles/rowDropDown.module.css';

const _NOTE_CHAR_LIMIT = 3000;

const CurrentProvisionDetails = ({ row, onClose, isDisabled, returnState }) => {
    const { handleSubmit } = useForm({});

    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [canBeSubmittedForFinanceRequest, setCanBeSubmittedForFinanceRequest] = useState('');
    const [notes, setNotes] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentProvision = useSelector((state) => state.entities.courseService.currentProvision);
    const provisionStatusDetails = useSelector(
        (state) => state.entities.directusService.provisionStatusDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadProvisionStatusDetails);
    }, []);

    useEffect(() => {
        dispatch(setCurrentProvision(row));
    }, [row]);

    useEffect(() => {
        if (currentProvision && Object.keys(currentProvision).length !== 0) {
            setCanBeSubmittedForFinanceRequest(false);
            if (currentProvision.participants && !currentProvision.participants.length < 1) {
                const index = currentProvision.participants.findIndex(
                    (item) => item.participantId === currentParticipant.id
                );
                if (index >= 0) {
                    const selectedProvisionStatus = provisionStatusDetails.find(
                        (item) => item.name.toLowerCase() === 'booked'
                    );
                    if (
                        currentProvision.participants[index].statusId !== selectedProvisionStatus.id // booked
                    ) {
                        setCanBeSubmittedForFinanceRequest(false);
                    }
                    setNotes(currentProvision.participants[index].notes);
                }
            }
        }
    }, [currentProvision.id]);

    useEffect(() => {
        if (successMessage === 'Participant has been submitted for finance request') {
            setCanBeSubmittedForFinanceRequest(false);
        }
    }, [successMessage]);

    //  FORM SUBMIT
    const onSubmit = () => {
        navigate('/create_financial_request', {
            state: {
                roles: loggedInUser.roles,
                accordionPanelFrom: returnState?.returnUrl ? returnState.returnUrl : 'courses',
                provision: row,
                returnState
            }
        });
    };

    return provisionStatusDetails.length < 1 ? (
        <LoadingSpinner content="Provision status not found" />
    ) : (
        <div
            onSubmit={(e) => {
                //  This stops the Submit event from bubbling up to the form enclosing this component
                e.preventDefault();
                e.stopPropagation();
            }}>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="form_start_PrimaryDetailsAdmin">
                <div className={dropdownClasses.rowDropdown}>
                    <Typography variant="h5">
                        {row.title} ({row.code})
                    </Typography>
                    <Typography variant="body1">{row.description}</Typography>
                    <p>
                        <VerifiedIcon sx={{ color: POLARIS_VICTORIA_BLUE }} />
                        <sup>{row.ofqualNumber}</sup>
                    </p>
                    <p>
                        <SellIcon sx={{ color: POLARIS_BLUE }} />{' '}
                        <sup>
                            {'£' +
                                row.costPerParticipant +
                                ' per participant - Payment Method is ' +
                                row.paymentMethod}
                        </sup>
                    </p>
                    <Typography variant="h6">Delivery Information</Typography>
                    <LabelledTextField
                        sx={{ marginBottom: '-20px' }}
                        disabled={true}
                        rows={3}
                        value={row.deliveryInformation || ''}
                    />
                    <Typography variant="h6">Qualification Required</Typography>
                    <LabelledTextField
                        sx={{ marginBottom: '-20px' }}
                        disabled={true}
                        rows={3}
                        value={row.qualificationRequired || ''}
                    />
                    <Typography variant="h6">Registration Methods</Typography>
                    <LabelledTextField
                        disabled={true}
                        rows={3}
                        value={row.registrationMethods || ''}
                    />
                    <TextAreaField
                        id={'notes'}
                        label={'Notes'}
                        disabled={true}
                        maxLength={_NOTE_CHAR_LIMIT}
                        value={notes || ''}
                        count={
                            notes ? `${notes.length}/${_NOTE_CHAR_LIMIT}` : `0/${_NOTE_CHAR_LIMIT}`
                        }
                    />
                    <Button
                        sx={{ marginTop: '-20px', marginBottom: '20px' }}
                        disabled={!!canBeSubmittedForFinanceRequest || isDisabled}
                        type="submit"
                        color="primary"
                        variant="contained"
                        data-testid="testIdSubmitButton1"
                        endIcon={<KeyboardArrowRightIcon />}>
                        {'Raise Finance Request'}
                    </Button>
                    <div className={dropdownClasses.exit} onClick={onClose}>
                        Close details
                        <CloseIcon />
                    </div>
                </div>
            </form>
        </div>
    );
};

CurrentProvisionDetails.propTypes = {
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    returnState: PropTypes.object
};

export default CurrentProvisionDetails;
